import { Component, ElementRef, HostListener, Input, model, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatSidenav } from '@angular/material/sidenav';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { CBOData, LinkedVOCLF, UserDetails, UserRoles } from '../models/user.model';
import { GlobalService } from '../services/global.service';
import { LocalStorageService } from '../services/local-storage.service';

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrl: './toolbar.component.scss',
    standalone: true,
    imports: [MatExpansionModule, MatListModule, TranslatePipe]
})
export class ToolbarComponent implements OnInit {
    userDetails = model.required<UserDetails>();
    activeRoleDisplayName = model.required<string>();
    @Input() sidenav: MatSidenav;

    showPopOver = false;
    rolesGroup = [];
    searchControl = new FormControl('');
    isOnline = true;

    @HostListener('document:click', ['$event'])
    closePopOver(event: Event) {
        const userProfileElement = this.eRef.nativeElement.querySelector('.header-right');

        if (!userProfileElement.contains(event.target)) {
            this.showPopOver = false;
        }
    }

    constructor(
        private eRef: ElementRef,
        private keycloakService: KeycloakService,
        private localService: LocalStorageService,
        private gs: GlobalService
    ) {}

    ngOnInit() {
        const storedRoleDisplayName = this.localService.get<string>('activeRoleDisplayName');
        if (storedRoleDisplayName) {
            this.activeRoleDisplayName.set(storedRoleDisplayName);
        }
        this.searchControl.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe((value) => {
            this.gs.toolbarSearchSubject.next(value || '');
        });
    }
    activeRoleDisplayNameClean(): string {
        const value = this.activeRoleDisplayName() || '';
        return value.replace(/\s*\(\d+\)$/, '');
    }

    togglePopOver() {
        this.showPopOver = !this.showPopOver;
    }

    changeRole(role: UserRoles): void {
        // Prepare geo input based on role type
        const geoInput: Partial<{ blockId: string; districtId: string; stateId: string }> = {};
        if (role.geoLevel === 'block') geoInput.blockId = `B-${role.blockId}`;
        else if (role.geoLevel === 'district') geoInput.districtId = `D-${role.districtId}`;
        else if (role.geoLevel === 'state') geoInput.stateId = `S-${role.stateId}`;

        const normalizedGeo = this.gs.getGeoLocation(geoInput, role.geoLevel);

        // For a CLF Bookkeeper entry, activate the selected federation as the active CLF.
        let cboOverride: { cboType: 'CLF' | 'VO'; activeFederationId: number; activeCBOData: CBOData } | undefined;
        if (role.roleOgName === 'clf-bookkeeper' && role.federationId) {
            const activeCBOData = (this.userDetails().cboData! as LinkedVOCLF[]).filter(
                (voOrClf) => voOrClf.federation_id === role.federationId
            );
            cboOverride = {
                cboType: 'CLF',
                activeFederationId: role.federationId,
                activeCBOData
            };
        }

        // Update user details
        this.userDetails.set({
            ...this.userDetails(),
            activeGeoLevel: role.geoLevel,
            activeRoleOgName: role.roleOgName,
            ...normalizedGeo,
            ...cboOverride
        });
        this.activeRoleDisplayName.set(role.roleDisplayName);
        this.localService.set('activeGeoLevel', role.geoLevel);
        this.localService.set('activeRoleOgName', role.roleOgName);
        this.localService.set('activeRoleDisplayName', this.activeRoleDisplayName());
        this.localService.set<UserDetails>('userDetails', this.userDetails());
        // Apply changes and redirect to root
        window.location.replace('/');
    }

    signOutUser() {
        if (window.Callback) {
            window.Callback.signOut();
        }
        this.localService.clearStorage();
        sessionStorage.clear();
        this.keycloakService.logout(window.location.origin);
    }
}
